import { ArrowLeftOutlined } from "@ant-design/icons";
import { Link, useHistory, useParams } from "react-router-dom";
import { Button, Spin } from "antd";
import { AgGridReact } from "ag-grid-react";
import { useMemo, useCallback, useContext, useRef, RefObject } from "react";
import { ColDef } from "ag-grid-enterprise";
import {
  truncatedTextCellRenderer,
  TruncatedTextWithTooltip
} from "components/cell-renders";
import ErrorBoundary from "components/error-boundary";
import { ProjectContext } from "context/ProjectProvider";
import { DateFilter } from "utils/dateutils";
import { downloadStringAsCSVFile, escapeCsvValue } from "utils/utils";
import {
  comparatorWithEmptyAtBottom,
  riskLevelComparator
} from "utils/grid-utils";
import useUpcomingMilestonesReport from "../hooks/upcoming-milestones-report";
import useAuditEventLog from "entity-app/utils/export-audit-log";

const GRID_CONSTANTS = {
  HEADER_HEIGHT: 36,
  GROUP_ROW_HEIGHT: 40,
  REGULAR_ROW_HEIGHT: 50
};

function TitleCellRenderer(
  props: Readonly<{
    data: { id: string; type: string; title: string };
    value: string;
    projectId: string;
  }>
) {
  const { data, value, projectId } = props;

  if (!data?.id) {
    return <TruncatedTextWithTooltip text={value} />;
  }

  return (
    <Link
      className="gridIdLink grow truncate text-left"
      to={{
        pathname: `/project/${projectId}/${
          data?.type === "Submittals" ? "submittals" : "materials"
        }/${data?.id}`
      }}
    >
      <TruncatedTextWithTooltip text={value} />
    </Link>
  );
}

function UserCellRenderer(
  props: Readonly<{
    data: { assignee_user: string; assignee_vendor: string };
  }>
) {
  const { data } = props;
  return (
    <div className="max-h-full overflow-hidden flex flex-col justify-center gap-y-1">
      <div className="leading-none font-normal">
        <TruncatedTextWithTooltip text={data?.assignee_user} />
      </div>
      <div className="leading-none font-normal text-ciq-gray-800">
        <TruncatedTextWithTooltip text={data?.assignee_vendor} />
      </div>
    </div>
  );
}

function PageHeader({
  data,
  onBtnExport,
  exportDisabled,
  backButton
}: Readonly<{
  data: { start_date: string; end_date: string } | null;
  onBtnExport: () => void;
  exportDisabled: boolean;
  backButton: () => void;
}>) {
  return (
    <div className="px-6 py-4 flex justify-between  items-center">
      <div className="flex shrink-0 items-center space-x-3">
        <ArrowLeftOutlined className="text-black" onClick={backButton} />
        <div className="flex flex-col pl-2">
          <div className="text-xl font-semibold">Procurement Follow-Ups</div>
          {data?.end_date && (
            <div className="overflow-hidden truncate shrink text-black text-opacity-80 text-xs font-normal">
              Upcoming Deadlines due before {data?.end_date}
            </div>
          )}
        </div>
      </div>
      <div>
        <Button onClick={onBtnExport} disabled={exportDisabled}>
          <span>Export as CSV</span>
        </Button>
      </div>
    </div>
  );
}

function ReportGrid({
  reportData,
  projectId,
  gridRef
}: Readonly<{
  reportData: any;
  projectId: string;
  gridRef: RefObject<AgGridReact<any>>;
}>) {
  const defaultColDef: ColDef = useMemo<ColDef>(() => {
    return {
      resizable: true,
      filter: false,
      suppressHeaderMenuButton: true,
      autoHeight: true,
      cellClass: (params) => {
        return params.node.group ? "cursor-pointer" : "";
      },
      comparator: comparatorWithEmptyAtBottom
    };
  }, []);

  const columnDefs = useMemo<ColDef[]>(() => {
    return [
      {
        field: "gc_user_full_name",
        headerName: "GC User",
        rowGroup: true,
        hide: true,
        sortable: true
      },
      {
        field: "type",
        headerName: "Type",
        rowGroup: true,
        hide: true
      },
      {
        field: "title",
        headerName: "Title",
        hide: true,
        sortable: true
      },
      {
        field: "milestone_state",
        headerName: "Status",
        sortable: true,
        cellRenderer: truncatedTextCellRenderer,
        minWidth: 200,
        maxWidth: 250
      },
      {
        field: "next_planned_deadline",
        headerName: "Next Deadline",
        sortable: true,
        cellRenderer: truncatedTextCellRenderer,
        sort: "asc",
        comparator: (valueA, valueB) => {
          return DateFilter.customComparator(valueA, valueB);
        },
        minWidth: 200,
        maxWidth: 250
      },
      {
        field: "risk_level",
        headerName: "Risk",
        sortable: true,
        comparator: riskLevelComparator,
        minWidth: 200,
        maxWidth: 250,
        cellClass: (params) => {
          return params?.value?.toLowerCase() === "high"
            ? "date-block-risk-high font-semibold"
            : "";
        }
      },
      {
        field: "assignee_user",
        headerName: "Ball in Court / Assignee",
        sortable: true,
        cellRenderer: UserCellRenderer,
        cellClass: "flex flex-col",
        minWidth: 220,
        maxWidth: 320
      }
    ];
  }, []);

  const autoGroupColumnDef = useMemo<ColDef>(() => {
    return {
      headerName: "Title",
      field: "title",
      cellRenderer: "agGroupCellRenderer",
      cellRendererParams: {
        innerRenderer: TitleCellRenderer,
        projectId
      },
      resizable: true,
      flex: 1,
      sortable: true,
      expandable: true,
      minWidth: 600
    };
  }, [projectId]);

  const onCellClicked = useCallback((params: any) => {
    if (params.node.group) {
      params.node.setExpanded(!params.node.expanded);
    }
  }, []);

  return (
    <div className="ag-theme-alpine custom-grid-header h-full w-full">
      <AgGridReact
        rowData={reportData}
        columnDefs={columnDefs}
        headerHeight={GRID_CONSTANTS.HEADER_HEIGHT}
        autoGroupColumnDef={autoGroupColumnDef}
        defaultColDef={defaultColDef}
        suppressContextMenu
        suppressCellFocus
        rowSelection={{
          mode: "singleRow",
          checkboxes: false,
          enableClickSelection: false
        }}
        suppressRowDrag
        suppressDragLeaveHidesColumns
        suppressMovableColumns
        getRowHeight={(params) => {
          return params.node.group
            ? GRID_CONSTANTS.GROUP_ROW_HEIGHT
            : GRID_CONSTANTS.REGULAR_ROW_HEIGHT;
        }}
        onCellClicked={onCellClicked}
        overlayNoRowsTemplate="No Upcoming Deadlines"
        tooltipShowMode="whenTruncated"
        ref={gridRef}
      />
    </div>
  );
}

function InAppUpcomingMilestonesReport() {
  const { projectId } = useParams<{ projectId: string }>();
  const history = useHistory();

  const {
    tokenRetrievalState,
    projectDetails,
    gqlClientForProject,
    isProjectArchived
  } = useContext(ProjectContext);

  const { saveAuditLogEvent } = useAuditEventLog(gqlClientForProject);

  const gridRef = useRef<AgGridReact>(null);

  const { reportData, fetchReportData, isLoading, error } =
    useUpcomingMilestonesReport(projectId, tokenRetrievalState?.token);

  const gotoReports = () => {
    history.replace(`/project/${projectId}/reports/project-reports`);
  };

  const loadingUI = (
    <div className="h-full w-full bg-white flex items-center justify-center">
      <Spin size="large" />
    </div>
  );

  const errorUI = (
    <div className="h-full w-full bg-white flex flex-col items-center justify-center gap-y-2">
      <div>Failed to fetch report</div>
      <Button size="middle" onClick={fetchReportData}>
        Retry
      </Button>
    </div>
  );

  const onBtnExport = useCallback(() => {
    const header = [
      "GC Reviewer/Representative",
      "Submittal/Material",
      "Title",
      "Status",
      "Next Deadline",
      "Risk",
      "Ball in Court/Assignee"
    ];
    const rowData: any[] = [];
    gridRef.current?.api.forEachNodeAfterFilterAndSort((node) => {
      if (!node.group && node.data) {
        rowData.push(node.data);
      }
    });

    const csvData = rowData.map((item: any) => {
      return [
        escapeCsvValue(item.gc_user_full_name),
        escapeCsvValue(
          item.type
            .replace("Submittals", "Submittal")
            .replace("Materials", "Material")
        ),
        escapeCsvValue(item.title),
        escapeCsvValue(item.milestone_state),
        escapeCsvValue(item.next_planned_deadline),
        escapeCsvValue(item.risk_level),
        escapeCsvValue(
          item.assignee_user
            ? `${item.assignee_user} (${item.assignee_vendor})`
            : ""
        )
      ];
    });
    // csvData is always an array (Array.prototype.map never returns null/undefined), so the `|| []` fallback is unreachable
    /* v8 ignore next */
    const csvContent = [header, ...(csvData || [])]
      .map((row: any) => row.join(","))
      .join("\n");
    if (!csvContent?.length || !csvData?.length) {
      return;
    }

    const filename = `Procurement Follow-ups_${projectDetails?.name}.csv`;
    downloadStringAsCSVFile(csvContent, filename);

    saveAuditLogEvent({
      eventType: "PROCUREMENT_FOLLOW_UPS_REPORT_EXPORT_CSV",
      featureType: "SUBMITTALS",
      filename
    });
  }, [projectDetails?.name, saveAuditLogEvent]);

  return (
    <div className="flex flex-col h-full w-full">
      {!isLoading && !error && (
        <div className="flex flex-col h-full bg-white">
          <ErrorBoundary>
            <PageHeader
              data={reportData}
              onBtnExport={onBtnExport}
              exportDisabled={
                !reportData?.formattedItems?.length || !!isProjectArchived
              }
              backButton={gotoReports}
            />
          </ErrorBoundary>
          <div className="grow">
            <ErrorBoundary>
              <ReportGrid
                reportData={reportData?.formattedItems}
                projectId={projectId}
                gridRef={gridRef}
              />
            </ErrorBoundary>
          </div>
        </div>
      )}
      {isLoading && loadingUI}
      {!isLoading && error && errorUI}
    </div>
  );
}

export default InAppUpcomingMilestonesReport;
